import { useCallback, useEffect, useRef, type WheelEvent } from "react";

const WHEEL_LINE_PX = 40;
const DELTA_MODE_LINE = 1;
const DELTA_MODE_PAGE = 2;

type HorizontalWheelEvent = Pick<
  WheelEvent<HTMLElement> | globalThis.WheelEvent,
  "ctrlKey" | "defaultPrevented" | "deltaMode" | "deltaX" | "deltaY" | "metaKey" | "preventDefault" | "shiftKey" | "stopPropagation"
>;

const normalizeWheelDelta = (event: HorizontalWheelEvent, container: HTMLElement) => {
  const multiplier = event.deltaMode === DELTA_MODE_LINE
    ? WHEEL_LINE_PX
    : event.deltaMode === DELTA_MODE_PAGE
      ? container.clientWidth
      : 1;

  return event.deltaY * multiplier;
};

const scrollElementFromWheel = (event: HorizontalWheelEvent, container: HTMLElement, enabled: boolean) => {
  if (!enabled || event.defaultPrevented) return;
  if (event.ctrlKey || event.metaKey || event.shiftKey) return;
  if (Math.abs(event.deltaY) <= Math.abs(event.deltaX)) return;

  const maxScrollLeft = container.scrollWidth - container.clientWidth;
  const delta = normalizeWheelDelta(event, container);

  event.preventDefault();
  event.stopPropagation();

  if (maxScrollLeft <= 0) return;

  container.scrollLeft = Math.min(maxScrollLeft, Math.max(0, container.scrollLeft + delta));
};

export function useHorizontalWheelScroll<T extends HTMLElement>(enabled = true) {
  return useCallback((event: WheelEvent<T>) => {
    scrollElementFromWheel(event, event.currentTarget, enabled);
  }, [enabled]);
}

export function useHorizontalWheelScrollRef<T extends HTMLElement>(enabled = true) {
  const ref = useRef<T | null>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element || !enabled) return;

    const handleWheel = (event: globalThis.WheelEvent) => {
      scrollElementFromWheel(event, element, enabled);
    };

    element.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      element.removeEventListener("wheel", handleWheel);
    };
  }, [enabled]);

  return ref;
}

export function useHorizontalWheelScrollCallbackRef<T extends HTMLElement>(enabled = true) {
  const cleanupRef = useRef<(() => void) | null>(null);

  return useCallback((element: T | null) => {
    cleanupRef.current?.();
    cleanupRef.current = null;

    if (!element || !enabled) return;

    const handleWheel = (event: globalThis.WheelEvent) => {
      scrollElementFromWheel(event, element, enabled);
    };

    element.addEventListener("wheel", handleWheel, { passive: false });
    cleanupRef.current = () => {
      element.removeEventListener("wheel", handleWheel);
    };
  }, [enabled]);
}
